import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PlayCircle, Zap } from "lucide-react";
import { api, useApi, errMsg, fmtDateTime } from "../lib/api";
import { Spinner, ErrorBox } from "../components/common";
import { AdminHead } from "./AdminLayout";

const mins = (a, b) => (a && b ? `${Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000))} min` : "—");
const notify = () => window.dispatchEvent(new Event("kh-health-changed"));

function CheckCard({ c, run }) {
  const pass = c.latest?.status === "passing";
  return (
    <article className={`kh-card p-5 ${pass ? "" : "border-[#B42318]"}`} data-testid={`health-check-${c.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div><h2 className="text-xl">{c.label}</h2><p className="text-xs capitalize text-[#5a5f69]">{c.severity} · 7-day uptime {c.uptime ?? "—"}%</p></div>
        <span className={`chip ${pass ? "bg-celadon-light text-[#2f5a4a]" : "bg-[#fde2df] text-[#9a1d14]"}`} data-testid={`health-status-${c.key}`}>{pass ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> : <XCircle className="h-3.5 w-3.5" aria-hidden="true" />}{pass ? "Passing" : "Failing"}</span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-1 text-xs"><dt className="text-[#5a5f69]">Last run</dt><dd>{fmtDateTime(c.latest?.created_at)}</dd><dt className="text-[#5a5f69]">Response time</dt><dd data-testid={`health-ms-${c.key}`}>{c.latest?.ms} ms</dd><dt className="text-[#5a5f69]">Message</dt><dd className="truncate" title={c.latest?.message}>{c.latest?.message}</dd></dl>
      <div className="mt-3" aria-label="7-day history"><p className="mb-1 text-xs text-[#5a5f69]">7-day history</p>
        <div className="flex gap-1">{c.history.map((d) => <div key={d.date} title={`${d.date}: ${d.pass} passed, ${d.fail} failed`} className={`h-6 flex-1 rounded ${d.fail ? "bg-[#e5867c]" : "bg-celadon"}`} />)}</div>
        <div className="mt-1 flex h-8 items-end gap-0.5">{c.recent.map((r) => <span key={r.id} title={`${r.ms} ms`} className={`w-full rounded-sm ${r.status === "passing" ? "bg-ink/40" : "bg-[#B42318]"}`} style={{ height: `${Math.min(100, 10 + r.ms / 6)}%` }} />)}</div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="btn-ghost btn-sm" onClick={() => run(`/admin/health/run/${c.key}`)} data-testid={`health-run-${c.key}`}><PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />Run now</button>
        <button className="btn-danger btn-sm" onClick={() => run(`/admin/health/simulate/${c.key}`, "Failure simulated — incident created and alert emailed")} data-testid={`health-simulate-${c.key}`}><Zap className="h-3.5 w-3.5" aria-hidden="true" />Simulate failure</button>
      </div>
    </article>
  );
}

function IncidentRow({ i, reload }) {
  const [notes, setNotes] = useState("");
  const act = async (path, body) => { try { await api.post(`/admin/health/incidents/${i.id}/${path}`, body); toast.success(path === "resolve" ? "Incident resolved" : "Incident acknowledged"); reload(); notify(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <li className="p-4" data-testid={`incident-${i.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`chip ${i.severity === "critical" ? "bg-[#fde2df] text-[#9a1d14]" : "bg-ochre-light text-ochre-dark"}`}>{i.severity === "critical" ? "Critical" : "Routine"}</span>
        <span className={`chip ${i.status === "resolved" ? "bg-celadon-light" : "bg-[#B42318] text-white"}`} data-testid={`incident-status-${i.id}`}>{i.status}</span>
        {i.simulated && <span className="chip">simulated</span>}
        <strong className="text-ink">{i.title}</strong>
      </div>
      <p className="mt-1 text-sm text-[#4a4f59]">{i.message}</p>
      <ol className="mt-2 grid gap-1 text-xs sm:grid-cols-3">
        <li>Opened: {fmtDateTime(i.opened_at)}</li>
        <li>Acknowledged: {fmtDateTime(i.acknowledged_at)} ({mins(i.opened_at, i.acknowledged_at)})</li>
        <li>Resolved: {fmtDateTime(i.resolved_at)} (time to fix {mins(i.opened_at, i.resolved_at)})</li>
      </ol>
      {i.notes && <p className="mt-1 text-xs italic">{i.notes}</p>}
      {i.status !== "resolved" && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!i.acknowledged_at && <button className="btn-ghost btn-sm" onClick={() => act("acknowledge", {})} data-testid={`incident-ack-${i.id}`}>Acknowledge</button>}
          <label className="sr-only" htmlFor={`rn-${i.id}`}>Resolution notes</label>
          <input id={`rn-${i.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Resolution notes (optional)" className="kh-input max-w-xs py-1.5" data-testid={`incident-notes-${i.id}`} />
          <button className="btn-primary btn-sm" onClick={() => act("resolve", { notes })} data-testid={`incident-resolve-${i.id}`}>Resolve</button>
        </div>
      )}
    </li>
  );
}

export default function AdminHealth() {
  const { data, loading, error, reload } = useApi("/admin/health");
  const [busy, setBusy] = useState(false);
  const run = async (path, msg) => {
    setBusy(true);
    try { const r = await api.post(path); toast[(Array.isArray(r.data) ? r.data.every((x) => x.status === "passing") : r.data.status === "passing") ? "success" : "error"](msg || "Check complete"); reload(); notify(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  return (
    <div aria-busy={busy}>
      <AdminHead title="System Health" intro={`Automated checks run every 5 minutes and on demand. Alerts are emailed to ${data.owner_email} (logged in the Outbox).`}>
        <button disabled={busy} onClick={() => run("/admin/health/run", "All checks run")} className="btn-primary" data-testid="health-run-all-btn">{busy ? "Running…" : "Run all checks now"}</button>
      </AdminHead>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{data.checks.map((c) => <CheckCard key={c.key} c={c} run={run} />)}</div>
      <section className="mt-10" aria-labelledby="inc-h">
        <h2 id="inc-h" className="mb-3 text-2xl">Incident timeline</h2>
        <ul className="divide-y divide-line rounded-2xl border border-line bg-white" data-testid="incident-list">{data.incidents.map((i) => <IncidentRow key={i.id} i={i} reload={reload} />)}</ul>
      </section>
    </div>
  );
}
